import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Menu from "@mui/material/Menu";
import AccountCircle from "@mui/icons-material/AccountCircle";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import MoreIcon from "@mui/icons-material/MoreVert";
import { useAuth } from "../../../../context/AuthProvider/useAuth";
import { useNavigate } from "react-router-dom";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import LogoutIcon from "@mui/icons-material/Logout";
import {
  Divider,
  PopoverOrigin,
  SxProps,
  Tooltip,
  Typography,
} from "@mui/material";
import { generalWhite } from "../../../../styles/colors";
import { UserRole } from "../../../../types/types";
import {
  CURRENT_SUBDOMAIN,
  DEMANDORIA_SUBDOMAINS,
} from "../../../../utils/stringUtils";
import { PrimaryButton } from "../../buttons/PrimaryButton";
import MobileMenuItem from "./MobileMenuItem";
import { useState } from "react";
import MenuItem from "./MenuItem";
import { handleLogout } from "../../../../utils/miscelaneous";
import { sharedStyles } from "@/styles/shared";
import LogoSvg from "@/assets/svg/logo.svg";
import TranslatorButton from '@/components/general/TranslatorButton';
import { useTranslation } from "react-i18next";

interface ClientNavbarProps {
  isUserComercial?: boolean;
  hideMidButton?: boolean;
  hideMenuButton?: boolean;
  hideCartButton?: boolean;
}

const anchorProps: PopoverOrigin = {
  vertical: "top",
  horizontal: "right",
};

const menuItemStyle = { color: generalWhite };

const ClientNavbar = (props: ClientNavbarProps) => {
  const { t } = useTranslation();
  const { hideCartButton, hideMenuButton, hideMidButton, isUserComercial } =
    props;

  const [mobileMoreAnchorEl, setMobileMoreAnchorEl] =
    useState<null | HTMLElement>(null);

  const isMobileMenuOpen = Boolean(mobileMoreAnchorEl);

  const auth = useAuth();
  const navigate = useNavigate();

  const handleMobileMenuClose = () => setMobileMoreAnchorEl(null);

  const handleMenuClose = () => handleMobileMenuClose();

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setMobileMoreAnchorEl(event.currentTarget);

  const handleLogoutClick = () => {
    window.confirm(t("components.clientNavbar.logoutConfirm")) &&
      handleLogout(auth, navigate);
    handleMenuClose();
  };

  const mobileMenu = (
    <Menu
      anchorEl={mobileMoreAnchorEl}
      anchorOrigin={anchorProps}
      keepMounted
      transformOrigin={anchorProps}
      open={isMobileMenuOpen}
      onClose={handleMobileMenuClose}
    >
      <MobileMenuItem
        icon={<AccountCircle sx={{ color: "text.primary" }} />}
        onClick={() => navigate("/user/details")}
        label={<Typography>{t("components.clientNavbar.myProfile")}</Typography>}
      />

      <MobileMenuItem
        icon={<CalendarMonthIcon sx={{ color: "text.primary" }} />}
        onClick={() => navigate("/my-appointments")}
        label={<Typography>{t("components.clientNavbar.appointments")}</Typography>}
      />

      <MobileMenuItem
        icon={<ReceiptLongIcon sx={{ color: "text.primary" }} />}
        onClick={() => navigate("/orders")}
        label={<Typography>{t("components.clientNavbar.orders")}</Typography>}
      />

      {!isUserComercial && !hideCartButton && (
        <MobileMenuItem
          icon={<ShoppingCartIcon sx={{ color: "text.primary" }} />}
          onClick={() => navigate("/cart")}
          label={<Typography>{t("components.clientNavbar.shoppingCart")}</Typography>}
        />
      )}

      <Divider />

      <MobileMenuItem
        icon={<LogoutIcon color="error" />}
        onClick={handleLogoutClick}
        label={<Typography color="error">{t("components.clientNavbar.logout")}</Typography>}
      />
    </Menu>
  );

  return (
    <>
      <Toolbar
        sx={{
          ...sharedStyles.horizontalContent,
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "primary.main",
        }}
      >
        <div
          onClick={() =>
            navigate(
              auth.user.role === UserRole.COMERCIAL ? "/appointments" : "/home"
            )
          }
          style={{ cursor: "pointer" }}
        >
          <img
            src={LogoSvg}
            alt="logo"
            height="100%"
            style={{ filter: "brightness(0) saturate(100%) invert(100%)" }}
          />
        </div>

        <Box sx={{ flexGrow: 1 }} />

        {!hideMidButton && (
          <Box>
            <PrimaryButton
              label={t("components.clientNavbar.advancedSearch")}
              icon={<ManageSearchIcon />}
              onClickAction={() => navigate("/search")}
              styles={styles.advancedSearchButton}
            />
          </Box>
        )}

        <Box sx={{ flexGrow: 1 }} />

        {!hideMenuButton && (
          <>
            <Box sx={{ display: { xs: "none", md: "flex" } }}>

            <TranslatorButton />

              {CURRENT_SUBDOMAIN !== DEMANDORIA_SUBDOMAINS[2] && (
                <>
                  <Tooltip title={t("components.clientNavbar.appointments")}>
                    <MenuItem
                      icon={<CalendarMonthIcon sx={menuItemStyle} />}
                      onClick={() => navigate("/my-appointments")}
                    />
                  </Tooltip>

                  <Tooltip title={t("components.clientNavbar.orders")}>
                    <MenuItem
                      icon={<ReceiptLongIcon sx={menuItemStyle} />}
                      onClick={() => navigate("/orders")}
                    />
                  </Tooltip>
                </>
              )}

              {!isUserComercial && !hideCartButton && (
                <Tooltip title={t("components.clientNavbar.shoppingCart")}>
                  <MenuItem
                    icon={<ShoppingCartIcon sx={menuItemStyle} />}
                    onClick={() => navigate("/cart")}
                  />
                </Tooltip>
              )}

              <Tooltip title={t("components.clientNavbar.myAccount")}>
                <MenuItem
                  icon={<AccountCircle sx={menuItemStyle} />}
                  onClick={() => navigate("/user/details")}
                />
              </Tooltip>
            </Box>

            <Box sx={{ display: { xs: "flex", md: "none" } }}>

              <TranslatorButton />

              <MenuItem
                icon={<MoreIcon sx={menuItemStyle} />}
                onClick={handleMobileMenuOpen}
              />
            </Box>
          </>
        )}
      </Toolbar>

      {!hideMenuButton && mobileMenu}
    </>
  );
};

export default ClientNavbar;

const styles = {
  logo: {
    color: generalWhite,
    textTransform: "none",
  } as SxProps,

  advancedSearchButton: {
    backgroundColor: "secondary.main",
    color: "primary.main",
    fontWeight: "bold",
    maxHeight: "40px",

    ":hover": {
      backgroundColor: "secondary.main",
    },
  } as SxProps,
};
